/**
 * Super Admin Module API - Auth Users
 * 
 * Operations for managing Supabase auth users via Edge Function.
 * This module calls the list-auth-users Edge Function which has
 * access to auth.users table (not accessible from client SDK).
 */

import { supabase } from '@/integrations/supabase/client';
import type { AuthUsersResponse, FetchAuthUsersOptions } from '../types';

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches auth users with profile status via Edge Function.
 * Only callable by super admins (enforced by Edge Function).
 * 
 * @param options - Filter, pagination, and search options
 * @returns List of auth users with profile information
 */
export async function fetchAuthUsers(
  options: FetchAuthUsersOptions = {}
): Promise<AuthUsersResponse> {
  const { profileFilter = 'all', page = 1, limit = 50, search = '' } = options;

  // Build query parameters
  const params = new URLSearchParams();
  params.set('profileFilter', profileFilter);
  params.set('page', page.toString());
  params.set('limit', limit.toString());
  if (search) params.set('search', search);

  const { data, error } = await supabase.functions.invoke('list-auth-users', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    body: null,
  });

  // Edge Function uses query params but invoke doesn't support them directly,
  // so we need to call via fetch instead
  const { data: session } = await supabase.auth.getSession();
  if (!session?.session?.access_token) {
    throw new Error('Not authenticated');
  }

  const functionUrl = `${import.meta.env.VITE_SUPABASE_URL || 'http://127.0.0.1:54321'}/functions/v1/list-auth-users?${params.toString()}`;
  
  const response = await fetch(functionUrl, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${session.session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  const result = await response.json();
  
  // Handle case where edge function returns error in body
  if (error || result.error) {
    throw new Error(error?.message || result.error || 'Failed to fetch auth users');
  }

  return result as AuthUsersResponse;
}
